import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useGetApiApprovalsCheck, useGetUserSettings } from '@sit/client-shared';
import { defaultLocale } from '@sit/core';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { formDevtoolsPlugin } from '@tanstack/react-form-devtools';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { PersistQueryClientOptions, PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import useIdentify from '@web/analytics/useIdentify';
import { useForceLogoutSITWeb } from '@web/auth/use-force-logout';
import { useIsAuthenticated } from '@web/auth/use-is-authenticated';
import { config } from '@web/config/env';
import CarbonI18nProvider from 'carbon-react/lib/components/i18n-provider';
import { JSX, lazy, PropsWithChildren, Suspense } from 'react';
import AuditProvider from './components/Audit/AuditProvider';
import JoinUsDialog from './components/JoinUsDialog';
import MaintenanceMode from './components/MaintenanceMode/MaintenanceMode';
import { ConnectedNotificationSocketProvider } from './components/Notifications/ConnectedNotificationSocketProvider';
import { NotificationGlobalHandler } from './components/Notifications/NotificationGlobalHandler';
import { NotificationHandlerProvider } from './components/Notifications/NotificationProvider';
import OverlayComponents from './components/OverlayComponents/OverlayComponents';
import { HeadManager } from './helpers/head-manager';
import { useInitializeClientShared } from './hooks/use-initialize-client-shared';
import { useCacheBustIndexDbIfDifferentUser } from './hooks/useBustCache';
import { useInitializeLaunchDarklyIdentifier } from './launchDarkly/launchDarklyIdentifierHook';
import { messages } from './lingui/en-US.po';
import { DEFAULT_CACHE_TIME, persister, queryClient } from './query-client';
import { useActivateUserLocale } from './services/lingui';
import { ThemeProvider } from './styles/theme';

const MouseTrap = lazy(() => import('./components/DeveloperSettings/DeveloperSettings'));

type AppProviderProps = Record<string, unknown>;

function useLoadAppState() {
  const isAuthenticated = useIsAuthenticated();
  useIdentify(isAuthenticated);

  useGetApiApprovalsCheck(
    {},
    {
      enabled: isAuthenticated,
      staleTime: 1000 * 60 * config.VITE_APP_MINUTES_TO_REFRESH_CPTI,
    },
  );

  useGetUserSettings({
    enabled: isAuthenticated,
  });

  useForceLogoutSITWeb();
}

function AppProvidersWithRouting({ children }: PropsWithChildren<AppProviderProps>) {
  useLoadAppState();

  return (
    <ConnectedNotificationSocketProvider>
      <NotificationHandlerProvider>
        <AuditProvider>
          <JoinUsDialog />
          <HeadManager />
          <NotificationGlobalHandler>{children}</NotificationGlobalHandler>
        </AuditProvider>
      </NotificationHandlerProvider>
    </ConnectedNotificationSocketProvider>
  );
}

const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
  persister,
  buster: '1',
  maxAge: DEFAULT_CACHE_TIME,
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => query.meta?.persist === true,
  },
};

// We have to load the default locale messages here because the i18n provider needs to be initialized with the default
i18n.loadAndActivate({ locale: defaultLocale, messages });

function InitWrapper({ children }: PropsWithChildren<AppProviderProps>) {
  useInitializeClientShared();
  useInitializeLaunchDarklyIdentifier();
  const { carbonLocale } = useActivateUserLocale();

  return <CarbonI18nProvider locale={carbonLocale}>{children}</CarbonI18nProvider>;
}

function PersistQueryClientInvalidator({ children }: PropsWithChildren<AppProviderProps>) {
  const { loading } = useCacheBustIndexDbIfDifferentUser();
  if (loading) return null;
  return <>{children}</>;
}

export default function AppProviders({ children }: PropsWithChildren<AppProviderProps>): JSX.Element {
  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      <I18nProvider i18n={i18n}>
        <PersistQueryClientInvalidator>
          <InitWrapper>
            <ThemeProvider>
              <MaintenanceMode>
                <Suspense fallback={null}>{import.meta.env.VITE_APP_SUPPORT_CHANGING_ORIGIN === 'true' && <MouseTrap />}</Suspense>
                <AppProvidersWithRouting>{children}</AppProvidersWithRouting>
                <OverlayComponents />
              </MaintenanceMode>
            </ThemeProvider>
            <TanStackDevtools
              plugins={[
                {
                  name: 'Query',
                  render: <ReactQueryDevtoolsPanel />,
                },
                {
                  name: 'Form',
                  render: formDevtoolsPlugin().render,
                },
              ]}
            />
          </InitWrapper>
        </PersistQueryClientInvalidator>
      </I18nProvider>
    </PersistQueryClientProvider>
  );
}
